import { db } from './firebase';
import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  increment,
  deleteDoc,
  getCountFromServer,
  startAfter,
  writeBatch,
  updateDoc,
} from 'firebase/firestore';

export const adminService = {
  // Upload JSON Logic
  uploadDataset: async (jsonData) => {
    // Expected format: Array of { season: "2025", date: "YYYY-MM-DD", words: [] }
    // We use date as the unique key since 'episode' number is missing in the source.

    // Normalize input
    const episodes = Array.isArray(jsonData) ? jsonData : [jsonData];

    let episodesAdded = 0;
    let wordsUpdated = 0;
    let newWordsMap = {}; // word -> count increment

    for (const ep of episodes) {
      // Validate keys
      if (!ep.date || !ep.season) {
        console.warn('Skipping invalid episode object (missing date/season):', ep);
        continue;
      }

      // 1. Check if episode exists
      // ID format: s2025_d2025-09-06
      const epId = `s${ep.season}_d${ep.date}`;
      const epRef = doc(db, 'episodes', epId);

      // Single read check (not transactional for speed, robust enough for admin)
      const snap = await getDoc(epRef);

      if (!snap.exists()) {
        // It's new!
        await setDoc(epRef, {
          season: ep.season,
          date: ep.date,
          title: ep.title || '',
          url: ep.url || '',
          words: ep.words || [],
          uploadedAt: new Date(),
        });
        episodesAdded++;

        // Tally words
        if (ep.words && Array.isArray(ep.words)) {
          ep.words.forEach((w) => {
            const cleanW = w.trim().toUpperCase();
            if (cleanW) newWordsMap[cleanW] = (newWordsMap[cleanW] || 0) + 1;
          });
        }
      }
    }

    // Step 2: Batch write word updates (reads in parallel, writes in chunks of 500)
    const uniqueWords = Object.keys(newWordsMap);
    const wordSnapshots = await Promise.all(uniqueWords.map((w) => getDoc(doc(db, 'words', w))));

    for (let i = 0; i < uniqueWords.length; i += 500) {
      const batch = writeBatch(db);
      for (let j = i; j < Math.min(i + 500, uniqueWords.length); j++) {
        const wordText = uniqueWords[j];
        const wordSnap = wordSnapshots[j];
        const wordRef = doc(db, 'words', wordText);
        const incrementBy = newWordsMap[wordText];

        if (!wordSnap.exists()) {
          batch.set(wordRef, {
            text: wordText,
            random: Math.random(),
            frequency: incrementBy,
            status: 'DUBBIO',
            corrette: 0,
            passate: 0,
            errate: 0,
            tempo: 0,
          });
        } else {
          const data = wordSnap.data();
          const updates = { frequency: increment(incrementBy) };
          if (data.random === undefined) updates.random = Math.random();
          batch.update(wordRef, updates);
        }
        wordsUpdated++;
      }
      await batch.commit();
    }

    return { episodesCount: episodesAdded, wordsCount: wordsUpdated };
  },

  // Get all episodes (or filter by season) - Removing limit for Season grouping
  getEpisodes: async (season = null) => {
    let q = collection(db, 'episodes');
    // If sorting needed, we need indexes. Let's start with basic fetch and sort client side
    // to avoid "Index Required" errors for every new season/date combo.
    // It's low scale admin, client sorting is safer/easier.

    if (season) {
      q = query(collection(db, 'episodes'), where('season', '==', season));
    }

    const snap = await getDocs(q);
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Client-side sort by date desc
    return docs.sort((a, b) => b.date.localeCompare(a.date));
  },

  updateEpisode: async (episodeId, data) => {
    const ref = doc(db, 'episodes', episodeId);
    await setDoc(ref, data, { merge: true });

    // NOTE provided data should include the updated 'words' array.
    // We aren't automatically re-calculating frequencies here to avoid complexity (decrementing old, incrementing new).
    // User might need to "Recalculate Stats" button separately if they edit words heavily.
  },

  getWordsByFrequency: async (limitCount = 50, lastDoc = null) => {
    let q = query(collection(db, 'words'), orderBy('frequency', 'desc'), limit(limitCount));

    if (lastDoc) {
      q = query(
        collection(db, 'words'),
        orderBy('frequency', 'desc'),
        startAfter(lastDoc),
        limit(limitCount),
      );
    }

    const snap = await getDocs(q);
    return {
      words: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      lastDoc: snap.docs[snap.docs.length - 1],
    };
  },

  getTotalWordCount: async () => {
    const coll = collection(db, 'words');
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  },

  resetDatabase: async () => {
    // Warning: This deletes ALL episodes and words.
    // Client-side deletion of collections requires reading all docs first.

    // 1. Delete Episodes
    const epSnap = await getDocs(collection(db, 'episodes'));
    const deleteEpPromises = epSnap.docs.map((d) => deleteDoc(doc(db, 'episodes', d.id)));
    await Promise.all(deleteEpPromises);

    // 2. Delete Words
    // Since there might be many, we should batch or do it carefully.
    // For now, simple Promise.all (might hit limits if >5000 words, but okay for dev)
    const wordSnap = await getDocs(collection(db, 'words'));
    const deleteWordPromises = wordSnap.docs.map((d) => deleteDoc(doc(db, 'words', d.id)));
    await Promise.all(deleteWordPromises);

    return { episodesDeleted: epSnap.size, wordsDeleted: wordSnap.size };
  },

  // Migration to add 'random' field to all words
  migrateRandomField: async (progressCallback) => {
    const wordsCol = collection(db, 'words');
    const snap = await getDocs(wordsCol);
    const total = snap.size;
    let processed = 0;

    // Process in batches of 500 (Firestore limit)
    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += 500) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + 500);

      chunk.forEach((d) => {
        // Only update if random field is missing or we want to re-randomize
        // For a one-time migration, we'll just set it.
        batch.set(d.ref, { random: Math.random() }, { merge: true });
      });

      await batch.commit();
      processed += chunk.length;
      if (progressCallback) progressCallback(processed, total);
    }

    return { total: processed };
  },

  // Initialize all word stats (status, corrette, passate, errate, tempo, random)
  initializeAllWordStats: async (progressCallback) => {
    const wordsCol = collection(db, 'words');
    const snap = await getDocs(wordsCol);
    const total = snap.size;
    let processed = 0;

    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += 500) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + 500);

      chunk.forEach((d) => {
        const data = d.data();
        const updates = {};

        if (data.random === undefined) updates.random = Math.random();
        if (data.status === undefined) updates.status = 'DUBBIO';
        if (data.corrette === undefined) updates.corrette = 0;
        if (data.passate === undefined) updates.passate = 0;
        if (data.errate === undefined) updates.errate = 0;
        if (data.tempo === undefined) updates.tempo = 0;

        if (Object.keys(updates).length > 0) {
          batch.update(d.ref, updates);
        }
      });

      await batch.commit();
      processed += chunk.length;
      if (progressCallback) progressCallback(processed, total);
    }

    return { total: processed };
  },

  getReportedWords: async () => {
    const q = query(collection(db, 'words'), where('status', '==', 'SEGNALATA'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  updateWordStatus: async (wordId, newStatus) => {
    const ref = doc(db, 'words', wordId);
    await updateDoc(ref, { status: newStatus });
  },

  deleteWord: async (wordId) => {
    const ref = doc(db, 'words', wordId);
    await deleteDoc(ref);
  },
};
